<template>
  <div class="rv-preview">
    <!-- Left explainer column -->
    <div class="rv-preview-left">
      <span class="rv-preview-back" @click="$emit('back')">
        <i class="fa fa-arrow-left"></i> Back to reviews
      </span>
      <h2 class="rv-preview-h2">What your patient sees</h2>
      <p class="rv-preview-lead">
        This is the one-tap page that opens from the text or email. Tap the stars to see how
        your gate routes each rating.
      </p>
      <div class="rv-preview-bullets">
        <div class="rv-preview-bullet">
          <div class="rv-preview-badge is-good">{{ gateThreshold === 5 ? '5' : '4+' }}</div>
          <div class="rv-preview-bullet-text">
            <b>{{ goodBulletLead }}</b> are guided straight to your Google page in one tap.
          </div>
        </div>
        <div class="rv-preview-bullet">
          <div class="rv-preview-badge is-low">&le;{{ gateThreshold - 1 }}</div>
          <div class="rv-preview-bullet-text">
            <b>{{ lowBulletLead }}</b> see a private form instead — routed to your office, never posted.
          </div>
        </div>
      </div>
      <p class="rv-preview-note">
        Previewing <b>{{ practiceName }}</b>'s live settings (gate: {{ gateThreshold === 5 ? '5 stars only' : '4 stars and up' }}).
        No message is sent — this is a simulation.
      </p>
    </div>

    <!-- Right device frame -->
    <div class="rv-preview-device-wrap">
      <div class="rv-preview-device">
        <div class="rv-preview-screen">
          <div class="rv-preview-notch"><div class="rv-preview-notch-pill"></div></div>

          <div class="rv-preview-content">
            <!-- Branded header -->
            <div class="rv-preview-brand">
              <img v-if="practiceLogoUrl" :src="practiceLogoUrl" :alt="practiceName" class="rv-preview-logo-img" />
              <template v-else>
                <div class="rv-preview-logo">{{ practiceInitial }}</div>
              </template>
              <div class="rv-preview-brand-name">{{ practiceName }}</div>
            </div>

            <!-- STEP: rate -->
            <div v-if="pStep === 'rate'" class="rv-preview-step">
              <h3 class="rv-preview-title">How was your visit?</h3>
              <p class="rv-preview-sub">
                Thanks for coming in, {{ sampleFirstName }}. How would you rate your experience?
              </p>
              <StarPicker :value="pRating" @select="onSelectRating" />
            </div>

            <!-- STEP: happy (Google prominent) -->
            <div v-else-if="pStep === 'happy'" class="rv-preview-step rv-preview-step-center">
              <div class="rv-preview-icon-circle">🎉</div>
              <div class="rv-preview-stars">{{ starString }}</div>
              <h3 class="rv-preview-title">So glad you enjoyed it!</h3>
              <p class="rv-preview-sub">
                Would you mind sharing that on Google? It takes about 15 seconds and means the world to our team.
              </p>
              <button type="button" class="rv-preview-google-btn" @click="onGoogle">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="#4285F4" stroke="#4285F4" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.12 2.12 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.12 2.12 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.12 2.12 0 0 0-1.973 0L6.396 21.11a.53.53 0 0 1-.77-.56l.881-5.139a2.12 2.12 0 0 0-.611-1.879L2.16 9.897a.53.53 0 0 1 .294-.905l5.165-.755a2.12 2.12 0 0 0 1.597-1.16z" />
                </svg>
                Review us on Google
              </button>
              <div class="rv-preview-small-link" @click="maybeLater">Maybe later</div>
              <!-- Nudged gate: the private option stays reachable from the happy branch too. -->
              <div class="rv-preview-small-link rv-preview-small-link-alt" @click="pStep = 'private'">
                Or send private feedback instead
              </div>
            </div>

            <!-- STEP: private (feedback form prominent) -->
            <div v-else-if="pStep === 'private'" class="rv-preview-step">
              <div class="rv-preview-stars rv-preview-stars-sm">{{ starString }}</div>
              <h3 class="rv-preview-title rv-preview-title-sm">We want to make this right</h3>
              <p class="rv-preview-sub rv-preview-sub-sm">
                This goes straight to our office — not online. What let you down?
              </p>
              <PrivateFeedbackForm :reasons="reasons" @submit="onFeedbackSubmit" />
              <!-- ⚖️ Nudged compliant gate: the public option stays reachable here too. -->
              <div v-if="googleUrl || hasAnyLink" class="rv-preview-small-link rv-preview-small-link-alt" @click="onGoogle">
                Prefer to post publicly? Review us on Google
              </div>
            </div>

            <!-- STEP: done -->
            <div v-else-if="pStep === 'done'" class="rv-preview-step rv-preview-step-center rv-preview-done">
              <div class="rv-preview-icon-circle"><i class="fa fa-check"></i></div>
              <h3 class="rv-preview-title">Thank you, {{ sampleFirstName }}</h3>
              <p class="rv-preview-sub">
                <template v-if="doneVariant === 'feedback'">Your feedback went straight to our office. We'll reach out personally to make it right.</template>
                <template v-else-if="doneVariant === 'public'">Thanks so much for sharing — it means the world to our team.</template>
                <template v-else>No problem — thanks for letting us know how we did!</template>
              </p>
              <div class="rv-preview-small-link" @click="restart">Start over</div>
            </div>

            <div class="rv-preview-footer">Powered by Teamio · for Open Dental</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
/**
 * ReviewsPatientPreview — the staff-facing "Preview patient view" (Reviews page).
 * Canonical plan: Documents/teamio-reviews.md (UI FIX — Preview patient view).
 *
 * Faithful recreation of the Claude Design's `isPatient` split screen: a left
 * explainer column + an interactive phone mockup that SIMULATES the real rating
 * page (src/pages/ReviewLanding). It reuses the exact leaf components the live
 * page uses (StarPicker, PrivateFeedbackForm) so the preview is representative.
 *
 * Pure client-side simulation — NO API writes. It reflects THIS office's real
 * settings (gate threshold, reason chips, configured Google link) + branding so
 * staff see what their own patients will experience. Tapping stars routes exactly
 * as the live nudged gate does (>= gateThreshold → Google; < → private form).
 */
import mixin from '../../../mixin.js';
import StarPicker from '../../ReviewLanding/components/StarPicker.vue';
import PrivateFeedbackForm from '../../ReviewLanding/components/PrivateFeedbackForm.vue';

const DEFAULT_REASONS = ['Wait time', 'Staff', 'Billing', 'Comfort', 'Communication'];
const SAMPLE_FIRST_NAME = 'Alex';

export default {
  name: 'ReviewsPatientPreview',
  components: { StarPicker, PrivateFeedbackForm },
  mixins: [mixin],

  data() {
    return {
      pStep: 'rate', // rate | happy | private | done
      pRating: 0,
      doneVariant: 'feedback', // feedback | public | later
      sampleFirstName: SAMPLE_FIRST_NAME,
    };
  },

  async created() {
    // The Settings tab loads settings into the store, but the preview may be
    // opened first — best-effort load so the gate/reasons/links are real.
    if (!this.settings && this.siteID) {
      try {
        await this.$store.dispatch('reviews/getReviewsSettings', { siteID: this.siteID });
      } catch (e) {
        /* fall back to defaults below */
      }
    }
  },

  computed: {
    settings() {
      return this.$store.state.reviews.settings;
    },
    gateThreshold() {
      const g = this.settings && this.settings.gateThreshold;
      return g === 5 ? 5 : 4;
    },
    reasons() {
      const r = this.settings && this.settings.privateFeedback && this.settings.privateFeedback.reasons;
      return Array.isArray(r) && r.length ? r : DEFAULT_REASONS;
    },
    reviewLinks() {
      return (this.settings && this.settings.reviewLinks) || {};
    },
    googleUrl() {
      return (this.reviewLinks.google && this.reviewLinks.google.url) || '';
    },
    hasAnyLink() {
      return ['google', 'facebook', 'yelp'].some((p) => this.reviewLinks[p] && this.reviewLinks[p].url);
    },
    starString() {
      return '★'.repeat(this.pRating || 0);
    },
    // Bullet copy derives from the real gate threshold so the explainer is accurate.
    goodBulletLead() {
      return this.gateThreshold === 5 ? 'Only 5 stars' : '4–5 stars';
    },
    lowBulletLead() {
      return this.gateThreshold === 5 ? '4 stars or fewer' : '3 stars or fewer';
    },
    // Practice name/logo — same resolution as ReviewsSettings.practiceName.
    practiceName() {
      const site = (this.sites || []).find((s) => s.SiteID === this.siteID);
      if (site && site.Name) return site.Name;
      if (this.allSettings && this.allSettings.GENERAL_PRACTICE_NAME && this.allSettings.GENERAL_PRACTICE_NAME.PropertyValue) {
        return this.allSettings.GENERAL_PRACTICE_NAME.PropertyValue;
      }
      return 'Your practice';
    },
    practiceInitial() {
      return (this.practiceName || 'P').trim().charAt(0).toUpperCase();
    },
    practiceLogoUrl() {
      const site = (this.sites || []).find((s) => s.SiteID === this.siteID);
      return (site && site.LogoUrl) || '';
    },
  },

  methods: {
    onSelectRating(n) {
      this.pRating = n;
      this.pStep = n >= this.gateThreshold ? 'happy' : 'private';
    },
    onFeedbackSubmit() {
      // Simulation only — no submit-private-feedback call.
      this.doneVariant = 'feedback';
      this.pStep = 'done';
    },
    onGoogle() {
      // Open the office's real Google review link in a new tab so staff can
      // verify it works; inert (no navigation) if none is configured yet.
      if (this.googleUrl) window.open(this.googleUrl, '_blank', 'noopener');
      this.doneVariant = 'public';
      this.pStep = 'done';
    },
    maybeLater() {
      this.doneVariant = 'later';
      this.pStep = 'done';
    },
    restart() {
      this.pStep = 'rate';
      this.pRating = 0;
    },
  },
};
</script>

<style lang="scss" scoped>
$primary: #0d5f54;
$neutral-950: #0b1220;
$neutral-900: #0f172a;
$neutral-500: #64748b;
$neutral-400: #94a3b8;
$border: #e2e8f0;

.rv-preview {
  display: flex;
  gap: 44px;
  align-items: flex-start;
  padding: 6px 8px 40px;
  flex-wrap: wrap;
}

/* Left explainer column */
.rv-preview-left {
  max-width: 340px;
  padding-top: 20px;
  flex: 1 1 280px;
}
.rv-preview-back {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: $neutral-500;
  cursor: pointer;
  margin-bottom: 22px;
  .fa { font-size: 12px; }
  &:hover { color: #334155; }
}
.rv-preview-h2 { font-size: 22px; font-weight: 800; letter-spacing: -0.02em; margin: 0 0 10px; color: $neutral-900; }
.rv-preview-lead { color: #475569; line-height: 1.6; font-size: 14px; margin: 0 0 22px; }
.rv-preview-bullets { display: flex; flex-direction: column; gap: 14px; }
.rv-preview-bullet { display: flex; gap: 12px; }
.rv-preview-badge {
  width: 26px; height: 26px; border-radius: 999px; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center; font-weight: 800; font-size: 12px;
  &.is-good { background: #e6f6f0; color: #0d9488; }
  &.is-low { background: #fef3c7; color: #b45309; }
}
.rv-preview-bullet-text { font-size: 13.5px; color: #334155; line-height: 1.5; b { color: $neutral-900; } }
.rv-preview-note { font-size: 12.5px; color: $neutral-400; line-height: 1.5; margin: 22px 0 0; }

/* Device frame */
.rv-preview-device-wrap { flex-shrink: 0; }
.rv-preview-device {
  width: 340px; height: 700px;
  background: $neutral-950;
  border-radius: 44px;
  padding: 12px;
  box-shadow: 0 24px 60px -20px rgba(12, 15, 14, 0.4);
}
.rv-preview-screen {
  width: 100%; height: 100%;
  background: #fff;
  border-radius: 34px;
  overflow: hidden;
  position: relative;
  display: flex;
  flex-direction: column;
}
.rv-preview-notch { height: 38px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.rv-preview-notch-pill { width: 110px; height: 26px; background: $neutral-950; border-radius: 0 0 16px 16px; }
.rv-preview-content { padding: 8px 26px 26px; flex: 1; overflow-y: auto; display: flex; flex-direction: column; }

/* Branded header */
.rv-preview-brand {
  display: flex; align-items: center; gap: 9px;
  padding-bottom: 20px; border-bottom: 1px solid #f1f5f9;
}
.rv-preview-logo {
  width: 30px; height: 30px; border-radius: 8px; background: $primary; color: #fff;
  font-weight: 800; display: flex; align-items: center; justify-content: center; flex-shrink: 0; font-size: 14px;
}
.rv-preview-logo-img { max-height: 34px; max-width: 180px; object-fit: contain; display: block; }
.rv-preview-brand-name { font-weight: 800; font-size: 14px; line-height: 1.2; color: $neutral-900; }

/* Steps */
.rv-preview-step { padding-top: 26px; flex: 1; }
.rv-preview-step-center { text-align: center; }
.rv-preview-done { padding-top: 48px; }
.rv-preview-title { font-size: 22px; font-weight: 800; letter-spacing: -0.02em; margin: 0 0 8px; text-align: center; color: $neutral-900; }
.rv-preview-title-sm { font-size: 19px; }
.rv-preview-sub { font-size: 13.5px; color: $neutral-500; line-height: 1.55; margin: 0 0 26px; text-align: center; }
.rv-preview-sub-sm { font-size: 13px; margin-bottom: 20px; }

.rv-preview-stars { color: #f59e0b; letter-spacing: 3px; font-size: 20px; margin-bottom: 14px; text-align: center; }
.rv-preview-stars-sm { letter-spacing: 2px; font-size: 17px; margin-bottom: 12px; }

.rv-preview-icon-circle {
  width: 64px; height: 64px; border-radius: 999px; background: #e6f2f0;
  display: flex; align-items: center; justify-content: center; margin: 0 auto 20px; font-size: 28px;
  .fa { font-size: 30px; color: $primary; }
}

.rv-preview-google-btn {
  width: 100%; cursor: pointer; font-family: inherit;
  background: #fff; border: 1px solid #cbd5e1; border-radius: 12px; padding: 13px;
  font-size: 14px; font-weight: 700; color: $neutral-900;
  display: flex; align-items: center; justify-content: center; gap: 10px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08); transition: border-color 0.12s ease;
  &:hover { border-color: $neutral-400; }
}

.rv-preview-small-link {
  margin-top: 16px; font-size: 12.5px; color: $neutral-400; cursor: pointer; text-align: center;
  &:hover { color: $neutral-500; text-decoration: underline; }
}
.rv-preview-small-link-alt { margin-top: 12px; }

.rv-preview-footer {
  margin-top: auto; padding-top: 20px; text-align: center;
  font-size: 10px; color: #cbd5e1; letter-spacing: 0.04em;
}
</style>
